import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faInstagram, faWhatsapp } from "@fortawesome/free-brands-svg-icons";
import { intToBrl } from "../../utils";
import { Amounts, Client, Quote, User } from "../../types";

const QuoteDesktop = ({
  quote,
  amounts,
  seller,
  client,
  equipmentMap,
}: {
  quote: Quote;
  amounts: Amounts;
  seller: User;
  client: Client;
  equipmentMap: Record<string, string>;
}) => (
  <div className="w-[670px] m-auto pb-3">
    <div className="p-2 border-b border-dashed border-gray-400 text-sm flex">
      <div className="flex-1 font-bold">
        Proposta comercial n° {quote.number}
      </div>
      <div>
        <span className="font-bold">Consultor:</span>{" "}
        {`${seller.firstName} ${seller.lastName}`}
      </div>
    </div>

    <div className="text-sm p-2 border-b border-dashed border-gray-400 flex flex-col gap-1">
      <div>
        <div className="w-28 float-left font-bold text-right">Cliente</div>
        <div className="ml-28 px-2">{client.name}</div>
      </div>

      <div>
        <div className="w-28 float-left font-bold text-right">Endereço</div>
        <div className="ml-28 px-2">
          {client.street}, {client.number}, {client.area}, {client.city},{" "}
          {client.state} - {client.zipcode}
        </div>
      </div>

      <div>
        <div className="w-28 float-left font-bold text-right">CPF/CNPJ</div>
        <div className="ml-28 px-2">{client.document}</div>
      </div>
    </div>

    {quote.itemsSold.length > 0 && (
      <div className="p-2 border-b border-dashed border-gray-400">
        <table className="text-sm w-full text-left">
          <thead className="border-b-2 border-gray-300">
            <tr>
              <th className="px-2">Produtos adquiridos</th>
              <th className="px-2 w-[1%]">Valor</th>
              <th className="px-2 w-[1%]">Qtd</th>
              <th className="px-2 w-[1%] text-right">Total</th>
            </tr>
          </thead>
          <tbody>
            {quote.itemsSold.map((item, i) => (
              <tr className="border-b" key={i}>
                <td className="px-2">{equipmentMap[item.equipmentUuid]}</td>
                <td className="px-2 whitespace-nowrap">
                  {intToBrl(item.price)}
                </td>
                <td className="px-2">{item.amount}</td>
                <td className="px-2 whitespace-nowrap text-right">
                  {intToBrl(item.price * item.amount)}
                </td>
              </tr>
            ))}

            {quote.discountSale > 0 && (
              <tr className="border-t-2 border-gray-300">
                <td className="px-2 text-gray-500 font-semibold" colSpan={3}>
                  Desconto
                </td>
                <td className="px-2 text-right font-bold whitespace-nowrap">
                  — {intToBrl(quote.discountSale)}
                </td>
              </tr>
            )}

            <tr className="border-t-2 border-gray-300">
              <td className="px-2 text-gray-500 font-semibold" colSpan={3}>
                Valor final
              </td>
              <td className="px-2 text-right font-bold whitespace-nowrap">
                {intToBrl(amounts.finalSale)}
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    )}

    {(quote.itemsRented.length > 0 || quote.feeMonitoring > 0) && (
      <div className="p-2 border-b border-dashed border-gray-400">
        <table className="text-sm w-full text-left">
          <thead className="border-b-2 border-gray-300">
            <tr>
              <th className="px-2">Equipamentos locados</th>
              <th className="px-2 w-[1%] whitespace-nowrap">Mensal</th>
              <th className="px-2 w-[1%]">Qtd</th>
              <th className="px-2 w-[1%] text-right">Total</th>
            </tr>
          </thead>
          <tbody>
            {quote.itemsRented.map((item, i) => (
              <tr className="border-b" key={i}>
                <td className="px-2">{equipmentMap[item.equipmentUuid]}</td>
                <td className="px-2 whitespace-nowrap">
                  {intToBrl(item.price)}
                </td>
                <td className="px-2">{item.amount}</td>
                <td className="px-2 whitespace-nowrap text-right">
                  {intToBrl(item.price * item.amount)}
                </td>
              </tr>
            ))}

            {quote.itemsRented.length === 0 && (
              <tr className="border-t-2 border-gray-300">
                <td colSpan={4} className="italic text-gray-400 px-2">
                  Nenhum
                </td>
              </tr>
            )}

            {quote.feeMonitoring > 0 && (
              <tr className="border-t-2 border-gray-300">
                <td className="px-2">Monitoramento</td>
                <td className="px-2 whitespace-nowrap">
                  {intToBrl(quote.feeMonitoring)}
                </td>
                <td className="px-2"></td>
                <td className="px-2 whitespace-nowrap text-right">
                  {intToBrl(quote.feeMonitoring)}
                </td>
              </tr>
            )}

            {quote.discountRent > 0 && (
              <tr className="border-t-2 border-gray-300">
                <td className="px-2 text-gray-500 font-semibold" colSpan={3}>
                  Desconto
                </td>
                <td className="px-2 text-right font-bold whitespace-nowrap">
                  — {intToBrl(quote.discountRent)}
                </td>
              </tr>
            )}

            <tr className="border-t-2 border-gray-300">
              <td className="px-2 text-gray-500 font-semibold" colSpan={3}>
                Mensalidade
              </td>
              <td className="px-2 text-right font-bold whitespace-nowrap">
                {intToBrl(amounts.finalRent)}
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    )}

    {(quote.feeInstall > 0 || quote.feeReview > 0) && (
      <div className="p-2 border-b border-dashed border-gray-400">
        <table className="text-sm w-full text-left">
          <thead className="border-b-2 border-gray-300">
            <tr>
              <th className="px-2">Encargos e taxas</th>
              <th className="w-[1%]"></th>
            </tr>
          </thead>
          <tbody>
            {quote.feeInstall > 0 && (
              <tr className="border-b last:border-0">
                <td className="px-2">Taxa de instalação</td>
                <td className="px-2 whitespace-nowrap font-bold">
                  {intToBrl(quote.feeInstall)}
                </td>
              </tr>
            )}

            {quote.feeReview > 0 && (
              <tr className="border-b last:border-0">
                <td className="px-2">Taxa de revisão</td>
                <td className="px-2 whitespace-nowrap font-bold">
                  {intToBrl(quote.feeReview)}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    )}

    <div className="border-2 m-2 text-sm break-inside-avoid">
      <div className="bg-gray-200 font-bold text-center p-1 mb-1">Resumo</div>

      {amounts.finalRent > 0 && (
        <div>
          <div className="w-36 font-semibold text-right float-left">
            Mensalidades:
          </div>
          <div className="ml-36 px-2 font-bold">
            {intToBrl(amounts.finalRent)}
          </div>
        </div>
      )}

      {quote.feeInstall > 0 && (
        <div>
          <div className="w-36 font-semibold text-right float-left">
            Taxa de instalação:
          </div>
          <div className="ml-36 px-2">
            <span className="font-bold">{intToBrl(quote.feeInstall)}</span> a
            ser pago antes do início do serviço
          </div>
        </div>
      )}

      {quote.feeReview > 0 && (
        <div>
          <div className="w-36 font-semibold text-right float-left">
            Taxa de revisão:
          </div>
          <div className="ml-36 px-2">
            <span className="font-bold">{intToBrl(quote.feeReview)}</span> a ser
            pago antes do início do serviço
          </div>
        </div>
      )}

      {amounts.finalSale > 0 && (
        <div>
          <div className="w-36 font-semibold text-right float-left">
            Produtos adquiridos:
          </div>
          <div className="ml-36 px-2">
            {quote.maxInstallments <= 1 && (
              <>
                Pagamento em até 30 dias:{" "}
                <span className="font-bold">{intToBrl(amounts.finalSale)}</span>
              </>
            )}

            {quote.maxInstallments === 2 && (
              <>
                Entrada de{" "}
                <span className="font-bold">
                  {intToBrl(Math.round(amounts.finalSale / 2))}
                </span>{" "}
                e uma parcela de{" "}
                <span className="font-bold">
                  {intToBrl(Math.round(amounts.finalSale / 2))}
                </span>
              </>
            )}

            {quote.maxInstallments >= 3 && (
              <>
                Entrada de{" "}
                <span className="font-bold">
                  {intToBrl(
                    Math.round(amounts.finalSale / quote.maxInstallments)
                  )}
                </span>{" "}
                + {quote.maxInstallments - 1} parcelas de{" "}
                <span className="font-bold">
                  {intToBrl(
                    Math.round(amounts.finalSale / quote.maxInstallments)
                  )}
                </span>
              </>
            )}
          </div>
        </div>
      )}

      {amounts.finalSale > 0 && (
        <div>
          <div className="w-36 font-semibold text-right float-left">
            Pagamento à vista:
          </div>
          <div className="ml-36 px-2">
            <span className="font-bold">6%</span> de desconto sobre o valor dos
            produtos adquiridos (
            <span className="font-bold">
              {intToBrl(Math.round(amounts.finalSale * 0.94))}
            </span>
            )
          </div>
        </div>
      )}

      {quote.note && (
        <div>
          <div className="w-36 font-semibold text-right float-left">
            Observações:
          </div>
          <div className="ml-36 px-2">{quote.note}</div>
        </div>
      )}

      <div className="bg-gray-200 font-semibold text-center text-xs p-1 mt-1">
        O consultor não está autorizado a receber nenhum valor — Dúvidas, ligue:
        27 3232-1000
      </div>
    </div>

    <div className="text-xs text-gray-500 leading-3 p-2 mb-4">
      Não inclui serviços de alvenaria, tubulação, solda ou elétrica.
      <br />
      Modelo do contrato disponível no site alarmsystem.com.br
      <br />{" "}
      <span className="font-semibold">
        Autorize essa proposta com o link recebido por email
      </span>
    </div>

    <div className="text-center pt-5 mb-16 print:hidden">
      <a
        href="https://www.instagram.com/alarmsystem.es/"
        target="_blank"
        rel="noreferrer"
        className="text-2xl px-2 text-gray-500 hover:text-gray-900"
      >
        <FontAwesomeIcon icon={faInstagram} />
      </a>

      <a
        href="https://wa.me/5527999481419"
        target="_blank"
        rel="noreferrer"
        className="text-2xl px-2 text-gray-500 hover:text-gray-900"
      >
        <FontAwesomeIcon icon={faWhatsapp} />
      </a>
    </div>
  </div>
);

export default QuoteDesktop;
